/**
 * ChangePassword.tsx — Forced password change page.
 *
 * Shown when an admin has reset the user's password and set force_password_change=true.
 * The user cannot skip this step. On success they are redirected to /dashboard.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import LockResetIcon from '@mui/icons-material/LockReset';
import SecurityIcon from '@mui/icons-material/Security';

import axios from 'axios';
import API_BASE from '../../config/api';
import { useAuth } from '../../context/AuthContext';

export default function ChangePassword() {
  const navigate          = useNavigate();
  const { refreshToken }  = useAuth();

  const [oldPw,   setOldPw]   = useState('');
  const [newPw,   setNewPw]   = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (newPw !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    if (newPw.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/change-password`, {
        old_password: oldPw,
        new_password: newPw,
      });
      setSuccess(true);
      // Refresh the token so force_password_change is cleared from JWT
      try { await refreshToken(); } catch { /* proceed anyway */ }
      setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'background.default',
      p: 2,
    }}>
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%', borderRadius: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <SecurityIcon sx={{ color: '#00E5FF', fontSize: 32 }} />
          <Typography variant="h5" fontWeight={700}>Shield</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LockResetIcon color="warning" />
          <Typography variant="h6" fontWeight={600}>Set a new password</Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Your password was reset by an administrator. You must set a new password before continuing.
        </Alert>

        {success ? (
          <Alert severity="success">Password updated! Redirecting…</Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Temporary password"
              type="password"
              value={oldPw}
              onChange={(e) => setOldPw(e.target.value)}
              required
              fullWidth
              autoFocus
              autoComplete="current-password"
            />
            <TextField
              label="New password"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              required
              fullWidth
              autoComplete="new-password"
              helperText="At least 8 characters"
            />
            <TextField
              label="Confirm new password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              fullWidth
              autoComplete="new-password"
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {loading ? 'Saving…' : 'Set new password'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
